import { useMemo } from "react";
import { Link } from "react-router";
import { AlertTriangle, ArrowRight, Info, ToggleRight } from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useListCarrierAddOnServices,
  useListCarrierAddOnServiceGrants,
  useListCarrierAddOnServiceAccess,
  useUpsertCarrierAddOnServiceAccess,
} from "@/api/carrier-add-on-services";
import { useGetCarriers, type Carrier } from "@/api/carriers";
import type {
  CarrierAddOnServiceAccess,
  CarrierAddOnServiceType,
} from "@/api.types.v2";

/**
 * Per-service carrier enablement. Every carrier, including the provider,
 * reflects persisted access. Granted label carriers get an
 * interactive toggle; non-granted carriers collapse into a single hint line
 * with a deep link to the Platform Grants screen — the grant is the ceiling,
 * this panel is the per-service switch within it.
 *
 * Reusable: this is what the Task 4 tabbed detail screen drops inside its
 * Enablement tab. Standalone access screen (this task) wraps it with a
 * service picker.
 */
export function ServiceEnablementPanel({
  serviceId,
  type = "PICKUP",
}: {
  serviceId: string;
  // The CarrierAddOnService type — drives which services list to scan and
  // which grant scope to query. Defaults to PICKUP so existing call sites
  // (pickup detail screen) keep working unchanged.
  type?: CarrierAddOnServiceType;
}) {
  const { data: carriersResp, isLoading: carriersLoading } = useGetCarriers();
  const carriers = carriersResp?.data;

  const {
    data: services,
    isLoading: servicesLoading,
    isError: servicesError,
  } = useListCarrierAddOnServices({ type });
  const service = services?.find((s) => s.id === serviceId);

  const {
    data: grants,
    isLoading: grantsLoading,
    isError: grantsError,
    refetch: refetchGrants,
  } = useListCarrierAddOnServiceGrants(
    service
      ? {
          providerCarrierId: service.providerCarrierId,
          serviceType: type,
        }
      : undefined
  );

  const {
    data: access,
    isLoading: accessLoading,
    isError: accessError,
    refetch: refetchAccess,
  } = useListCarrierAddOnServiceAccess(
    service ? { carrierAddOnServiceId: service.id } : undefined
  );

  const upsertAccess = useUpsertCarrierAddOnServiceAccess();
  const pending = upsertAccess.isPending;

  // labelCarrierId set of carriers the platform granted this provider.
  const grantedLabelCarrierIds = useMemo(() => {
    const set = new Set<string>();
    for (const g of grants ?? []) set.add(g.labelCarrierId);
    return set;
  }, [grants]);

  // labelCarrierId → access row. The row is kept so the toggle reads `enabled`
  // (a row can exist with enabled: false).
  const accessByLabelCarrier = useMemo(() => {
    const map = new Map<string, CarrierAddOnServiceAccess>();
    for (const a of access ?? []) map.set(a.labelCarrierId, a);
    return map;
  }, [access]);

  const initialLoading =
    servicesLoading || carriersLoading || grantsLoading || accessLoading;

  if (initialLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (servicesError || !service) {
    // A failed services fetch (or unknown serviceId) must NOT render as an
    // empty toggle list — that would misrepresent which carriers are enabled.
    return (
      <div className="text-center p-8 text-muted-foreground">
        <AlertTriangle className="mx-auto h-10 w-10 mb-2 text-destructive opacity-70" />
        <p>Couldn&apos;t load this service.</p>
      </div>
    );
  }

  if (grantsError || accessError) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        <AlertTriangle className="mx-auto h-10 w-10 mb-2 text-destructive opacity-70" />
        <p>Couldn&apos;t load carrier enablement.</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => {
            if (grantsError) void refetchGrants();
            if (accessError) void refetchAccess();
          }}
        >
          Try again
        </Button>
      </div>
    );
  }

  const allCarriers = carriers ?? [];
  const provider = allCarriers.find((c) => c.id === service.providerCarrierId);

  // Non-provider granted carriers — interactive rows.
  const grantedRows = allCarriers.filter(
    (c) =>
      c.id !== service.providerCarrierId && grantedLabelCarrierIds.has(c.id)
  );

  // Carriers without a platform grant — collapsed into a single hint line.
  const nonGranted = allCarriers.filter(
    (c) => !grantedLabelCarrierIds.has(c.id)
  );

  const handleToggle = (labelCarrier: Carrier, nextEnabled: boolean) => {
    upsertAccess.mutate(
      {
        carrierAddOnServiceId: service.id,
        labelCarrierId: labelCarrier.id,
        enabled: nextEnabled,
      },
      {
        onSuccess: () =>
          toast.success(nextEnabled ? "Service enabled." : "Service disabled."),
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(
            e.response?.data?.error ?? "Failed to update enablement."
          );
        },
      }
    );
  };

  if (allCarriers.length === 0) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        <ToggleRight className="mx-auto h-10 w-10 mb-2 opacity-50" />
        <p>No carriers to enable.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Label carrier</TableHead>
            <TableHead className="w-[1%] text-right">Enabled</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {provider && (
            <TableRow key={provider.id}>
              <TableCell>
                {provider.name}
                <span className="ml-2 text-xs text-muted-foreground">
                  (provider)
                </span>
              </TableCell>
              <TableCell className="text-right">
                <Switch
                  checked={
                    accessByLabelCarrier.get(provider.id)?.enabled ?? false
                  }
                  disabled={pending || !grantedLabelCarrierIds.has(provider.id)}
                  onCheckedChange={(next) => handleToggle(provider, next)}
                  aria-label={`Enable ${service.name} for ${provider.name}`}
                />
              </TableCell>
            </TableRow>
          )}
          {grantedRows.map((c) => {
            const enabled = accessByLabelCarrier.get(c.id)?.enabled ?? false;
            return (
              <TableRow key={c.id}>
                <TableCell>{c.name}</TableCell>
                <TableCell className="text-right">
                  <Switch
                    checked={enabled}
                    disabled={pending}
                    onCheckedChange={(next) => handleToggle(c, next)}
                    aria-label={`Enable ${service.name} for ${c.name}`}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {nonGranted.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground px-1">
          <Info className="h-4 w-4 shrink-0 opacity-70" />
          <span>
            {nonGranted.map((c) => c.name).join(", ")} not granted access to
            this service.
          </span>
          <Button asChild variant="link" size="sm" className="h-auto p-0">
            <Link to="/add-on-services/grants">
              Request grant
              <ArrowRight className="ml-1 h-3 w-3" />
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
